import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';

type Player = Database['public']['Tables']['player']['Row'];
type PlayerInsert = Database['public']['Tables']['player']['Insert'];
type PlayerUpdate = Database['public']['Tables']['player']['Update'];

type FormatNameOptions = { isJapanese?: boolean; maxLength?: number; short?: boolean };

export const playerModelV2 = {
  /**
   * Check if a federation uses Japanese name formatting based on currency
   * @param currency - The federation's default_currency value
   * @returns true if the federation is Japanese
   */
  isJapaneseFederation(currency: string | null): boolean {
    return currency === 'jpy';
  },

  /**
   * Safely extract the first character from a string, handling surrogate pairs and encoding issues
   * @param str - String to extract character from
   * @returns First character or empty string if not found
   */
  safeFirstChar(str: string | null): string {
    if (!str) return '';

    // Handle surrogate pairs properly using codePointAt
    const firstCodePoint = str.codePointAt(0);

    if (firstCodePoint) {
      // Check if it's a replacement character (U+FFFD)
      if (firstCodePoint === 65533) {
        // Try to find any valid character in the string
        for (let i = 0; i < str.length; i++) {
          const codePoint = str.codePointAt(i);
          if (codePoint && codePoint !== 65533) {
            return String.fromCodePoint(codePoint);
          }
        }
        return '';
      }

      return String.fromCodePoint(firstCodePoint);
    }

    return '';
  },
  /**
   * Format a player's name with script-based ordering
   * @param firstName - First name
   * @param lastName - Last name
   * @param alias - Optional alias/username for fallback
   * @param options - Optional formatting hints
   * @param options.isJapanese - Force Japanese formatting (full-width space). Use when federation is known to be Japanese but name is pure kanji.
   * @param options.short - Space-constrained form: a Latin name with both parts becomes "F. Last"
   *   (first initial + ". " + last); a name with only one part returns that part; a CJK name is
   *   unchanged (family-first — one given-name character already reads as short). Mirrors the
   *   mobile app's `formatShortName` (playpulse-app `lib/core/format/format_name.dart`); keep the
   *   two in step (docs/CONVENTIONS.md, "Formatting parity"). Applied before `maxLength`, so the
   *   two compose.
   * @returns Formatted name string (Last+First for Chinese/Japanese names, First+Last for others)
   */
  formatName(
    firstName: string | null = '',
    lastName: string | null = '',
    alias?: string | null,
    options?: FormatNameOptions,
  ): string {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();

    // If no names provided, return alias or fallback
    if (!first && !last) {
      return alias || 'Unknown Player';
    }

    // Determine name order based on the script/language of the name content
    const combinedName = first + last;
    const hasChineseScript = /[\u4e00-\u9fff]/.test(combinedName);
    const hasJapaneseScript = /[\u3040-\u309f\u30a0-\u30ff]/.test(combinedName);
    // isJapanese hint only applies when name has CJK characters (pure kanji that can't be
    // auto-detected as Japanese). English names in Japanese federations stay Western order.
    const isJapanese = hasJapaneseScript || (options?.isJapanese && hasChineseScript);

    let result: string;

    if (hasChineseScript || isJapanese) {
      // CJK names: Last Name + First Name
      // Full-width space for Japanese, no space for Chinese
      const separator = isJapanese ? '\u3000' : '';
      result = last && first ? `${last}${separator}${first}` : `${last}${first}`.trim();
    } else {
      // English and other scripts: First Name + Last Name (with space)
      result = `${first} ${last}`.trim();
    }

    // Short form: only a two-part non-CJK name abbreviates. A single part is
    // already as short as it gets, and a CJK name is left family-first.
    if (options?.short && !hasChineseScript && !isJapanese && first && last) {
      result = `${this.safeFirstChar(first)}. ${last}`;
    }

    result = result || alias || 'Unknown Player';

    // Truncate long names to "F. Surname" format when maxLength is specified
    const maxLength = options?.maxLength;
    if (maxLength && result.length > maxLength) {
      if (hasChineseScript || isJapanese) {
        // CJK: truncate with ellipsis
        result = result.slice(0, maxLength - 1) + '…';
      } else if (first && last) {
        // "F. Surname" format
        const abbreviated = `${this.safeFirstChar(first)}. ${last}`;
        if (abbreviated.length <= maxLength) {
          result = abbreviated;
        } else {
          // Still too long — truncate with ellipsis
          result = abbreviated.slice(0, maxLength - 1) + '…';
        }
      } else {
        result = result.slice(0, maxLength - 1) + '…';
      }
    }

    return result;
  },

  /**
   * Get initials from name fields with script-based ordering
   * @param firstName - First name
   * @param lastName - Last name
   * @param alias - Optional alias/username for fallback
   * @returns Initials string (e.g., "JD" for John Doe, "李王" for 李王小明)
   */
  getInitials(
    firstName: string | null = '',
    lastName: string | null = '',
    alias?: string | null,
  ): string {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();

    // If no names provided, fallback to alias initials
    if (!first && !last) {
      return (alias || '').slice(0, 2).toUpperCase() || '?';
    }

    // For Chinese/Japanese names, use first character of each name
    const combinedName = first + last;
    const hasChineseScript = /[\u4e00-\u9fff]/.test(combinedName);
    const hasJapaneseScript = /[\u3040-\u309f\u30a0-\u30ff]/.test(combinedName);

    if (hasChineseScript || hasJapaneseScript) {
      // Chinese/Japanese names: Use first character of each name (Last + First)
      const firstInitial = this.safeFirstChar(first);
      const lastInitial = this.safeFirstChar(last);

      // Fallback to alias if characters are not available
      const finalFirstInitial = firstInitial || alias?.[0]?.toUpperCase() || '?';
      const finalLastInitial = lastInitial || alias?.[1]?.toUpperCase() || '?';

      const result = finalLastInitial + finalFirstInitial;
      return result || (alias || '').slice(0, 2).toUpperCase() || '?';
    } else {
      // English and other scripts: Use first character of first and last name
      const firstInitial = first?.[0]?.toUpperCase() || '';
      const lastInitial = last?.[0]?.toUpperCase() || '';
      const result = firstInitial + lastInitial;
      return result || (alias || '').slice(0, 2).toUpperCase() || '?';
    }
  },

  /**
   * Get the full name of a player with script-based formatting (convenience method)
   * @param player - Player object
   * @returns Full name string, or empty string if no name available
   */
  getFullName(player: Player, options?: FormatNameOptions): string {
    return this.formatName(player.first_name, player.last_name, null, options);
  },

  /**
   * Get the display name for a player with fallback to username (convenience method)
   * @param player - Player object
   * @returns Display name (full name if available, otherwise username)
   */
  getDisplayName(player: Player, options?: FormatNameOptions): string {
    const fullName = this.formatName(player.first_name, player.last_name, player.username, options);
    return fullName || player.username || 'Unknown Player';
  },

  /**
   * Get a player by ID
   * @param client - Supabase client
   * @param playerId - Player ID
   * @returns Player object or null
   */
  async getById(client: SupabaseClient<Database>, playerId: string): Promise<Player | null> {
    const { data, error } = await client
      .from('player')
      .select('*')
      .eq('id', playerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get a player by username
   * @param client - Supabase client
   * @param username - Username
   * @returns Player object or null
   */
  async getByUsername(client: SupabaseClient<Database>, username: string): Promise<Player | null> {
    const { data, error } = await client
      .from('player')
      .select('*')
      .eq('username', username)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get a player by user ID
   * @param client - Supabase client
   * @param userId - User ID
   * @returns Player object or null
   */
  async getByUserId(client: SupabaseClient<Database>, userId: string): Promise<Player | null> {
    const { data, error } = await client
      .from('player')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get multiple players by IDs
   * @param client - Supabase client
   * @param playerIds - Array of player IDs
   * @returns Array of player objects
   */
  async getByIds(client: SupabaseClient<Database>, playerIds: string[]): Promise<Player[]> {
    if (playerIds.length === 0) return [];

    const { data, error } = await client.from('player').select('*').in('id', playerIds);

    if (error) throw error;
    return data || [];
  },

  /**
   * Update a player
   * @param client - Supabase client
   * @param playerId - Player ID
   * @param updates - Player updates
   * @returns Updated player object
   */
  async update(
    client: SupabaseClient<Database>,
    playerId: string,
    updates: PlayerUpdate,
  ): Promise<Player> {
    const { data, error } = await client
      .from('player')
      .update(updates)
      .eq('id', playerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create a new player
   * @param client - Supabase client
   * @param playerData - Player data
   * @returns Created player object
   */
  async create(client: SupabaseClient<Database>, playerData: PlayerInsert): Promise<Player> {
    const { data, error } = await client.from('player').insert(playerData).select().single();

    if (error) throw error;
    return data;
  },

  /**
   * Search players by name or username
   * @param client - Supabase client
   * @param searchTerm - Search term
   * @param limit - Maximum number of results
   * @returns Array of matching players
   */
  async search(
    client: SupabaseClient<Database>,
    searchTerm: string,
    limit: number = 10,
  ): Promise<Player[]> {
    const { data, error } = await client
      .from('player')
      .select('*')
      .or(
        `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`,
      )
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Get player details with federation data and custom data for admin use
   * @param client - Supabase admin client
   * @param playerId - Player ID
   * @returns Player with federation and custom data, or null if not found
   */
  async getDetailsWithFederationData(
    client: SupabaseClient<Database>,
    playerId: string,
  ): Promise<{
    id: string;
    first_name: string;
    last_name: string;
    username: string | null;
    current_rating: number | null;
    gender: string | null;
    profile_photo: string | null;
    federation_user: {
      id: string;
      federation_id: string;
      federation_user_custom_data: {
        custom_data: Record<string, unknown> | null;
      } | null;
    } | null;
  } | null> {
    const { data, error } = await client
      .from('player')
      .select(
        `
        id,
        first_name,
        last_name,
        username,
        current_rating,
        gender,
        profile_photo,
        federation_user(
          id,
          federation_id,
          federation_user_custom_data(
            custom_data
          )
        )
      `,
      )
      .eq('id', playerId)
      .maybeSingle();

    if (error) throw error;

    if (!data) return null;

    // Handle federation_user being an array (take first one) or null
    const federationUser = Array.isArray(data.federation_user)
      ? data.federation_user[0] || null
      : data.federation_user;

    return {
      ...data,
      federation_user: federationUser
        ? {
            ...federationUser,
            federation_user_custom_data: federationUser.federation_user_custom_data
              ? {
                  custom_data: federationUser.federation_user_custom_data.custom_data as Record<
                    string,
                    unknown
                  > | null,
                }
              : null,
          }
        : null,
    };
  },
};
